import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { ChartRendererProps } from '../../types/chart-renderer-props';
import { toApexAxisChartSeries, toApexCategories } from '../../utils/to-apex-series';
import { ChartContainer } from '../shared/chart-container.component';
import { useUnknownChartSeriesLabel } from '../shared/use-chart-series-display-name';
import { LineChartLabelFormat, LineChartLabelMode, getLineChartOptions } from './line-chart-options';

export interface LineChartProps extends ChartRendererProps {
    showArea?: boolean;
    showLegend?: boolean;
    showLabels?: LineChartLabelMode;
    labelFormat?: LineChartLabelFormat;
    tooltipShared?: boolean;
    tooltipSeriesLabel?: string;
    /** Label for `data.reference` horizontal annotation when present. */
    referenceLabel?: string;
}

export const LineChart = ({
    data,
    className,
    minHeight = 160,
    showArea = true,
    showLegend,
    showLabels = true,
    labelFormat,
    tooltipShared,
    tooltipSeriesLabel,
    referenceLabel,
    chartOptions
}: LineChartProps) => {
    const unknownLabel = useUnknownChartSeriesLabel();
    const series = useMemo(() => toApexAxisChartSeries(data, unknownLabel), [data, unknownLabel]);
    const categories = useMemo(() => toApexCategories(data), [data]);
    const seriesMax = useMemo(() => Math.max(0, ...data.series.flatMap((item) => item.data.map((point) => point.value))), [data.series]);

    return (
        <ChartContainer
            className={className}
            minHeight={minHeight}>
            {(size) => (
                <ReactApexChart
                    options={{
                        ...getLineChartOptions({
                            categories,
                            height: size.height,
                            width: size.width,
                            showArea,
                            showLegend,
                            showLabels,
                            labelFormat,
                            tooltipShared,
                            tooltipSeriesLabel,
                            reference: data.reference,
                            referenceLabel,
                            seriesMax
                        }),
                        ...chartOptions
                    }}
                    series={series}
                    type={showArea ? 'area' : 'line'}
                    height={size.height}
                    width={size.width}
                />
            )}
        </ChartContainer>
    );
};
